import React, { useEffect, useState } from "react";
import { adminAPI } from "../../services/api/admin";

// Per-account plan-limit overrides. Each row shows the plan default and lets an
// admin override it (blank = revert to the plan default; 0 = block all).
const LIMITS = [
  { key: "max_users", label: "Max users (seats)" },
  { key: "max_concurrent_runs", label: "Max concurrent runs" },
  { key: "max_agent_metrics", label: "Max metrics per agent" },
  { key: "max_global_metrics", label: "Max global metrics" },
  { key: "max_copilot_daily_turns", label: "Max copilot turns / day" },
];

export default function AdminLimitOverridesPanel({ account, onChange }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState(null);

  // Seed the inputs from the account's current overrides whenever it changes.
  useEffect(() => {
    const seeded = {};
    LIMITS.forEach(({ key }) => {
      const v = account?.limit_overrides?.[key];
      seeded[key] = v === undefined || v === null ? "" : String(v);
    });
    setValues(seeded);
    setMsg(null);
  }, [account?.id, account?.limit_overrides]);

  const planDefault = (key) => {
    const v = account?.plan_limits?.[key];
    return v === undefined || v === null ? "Unlimited" : v;
  };

  const save = async (key) => {
    setSaving(key);
    setMsg(null);
    try {
      // Empty string clears the override (backend reverts to the plan default).
      await adminAPI.setLimitOverride(account.id, key, values[key] === "" ? "" : Number(values[key]));
      setMsg({ type: "success", text: "Limit updated" });
      onChange?.();
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.error || "Failed to update limit" });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">Plan limit overrides</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Override this account's pooled limits. Leave blank to use the plan default; set 0 to block.
      </p>

      {msg && (
        <p className={`mt-3 text-sm ${msg.type === "error" ? "text-red-600" : "text-green-700"}`}>
          {msg.text}
        </p>
      )}

      <div className="mt-4 space-y-4">
        {LIMITS.map(({ key, label }) => (
          <div key={key} className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
              <input
                type="number"
                min="0"
                step="1"
                value={values[key] ?? ""}
                onChange={(e) => setValues((p) => ({ ...p, [key]: e.target.value }))}
                placeholder={`Plan default: ${planDefault(key)}`}
                className="block w-full rounded-lg border-input shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => save(key)}
              disabled={saving === key}
              className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-black hover:bg-gray-800 disabled:opacity-50"
            >
              {saving === key ? "Saving…" : "Save"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
